import { useState } from 'react'
import { Plus, Search, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import {
  useLaboratorios, useCreateLaboratorio, useUpdateLaboratorio, useDeleteLaboratorio, useGrupos,
} from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { LaboratorioFormDialog } from './LaboratorioFormDialog'
import type { Laboratorio } from '@/modules/multichannel/types/configuracion'

export function LaboratoriosTab() {
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Laboratorio | null>(null)

  const { data: laboratorios, isLoading } = useLaboratorios(
    showInactive ? undefined : { activo: true }
  )
  const { data: grupos } = useGrupos()
  const createMutation = useCreateLaboratorio()
  const updateMutation = useUpdateLaboratorio()
  const deleteMutation = useDeleteLaboratorio()

  const filtered = (laboratorios || []).filter(l =>
    !search || l.nombre.toLowerCase().includes(search.toLowerCase())
  )

  const handleCreate = () => { setEditing(null); setDialogOpen(true) }
  const handleEdit = (lab: Laboratorio) => { setEditing(lab); setDialogOpen(true) }

  const handleDelete = async (lab: Laboratorio) => {
    if (window.confirm(`Eliminar laboratorio "${lab.nombre}"?`)) {
      await deleteMutation.mutateAsync(lab.id)
    }
  }

  const handleToggleActivo = async (lab: Laboratorio) => {
    await updateMutation.mutateAsync({ id: lab.id, data: { activo: !lab.activo } })
  }

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) setEditing(null)
  }

  return (
    <div className="space-y-4">
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Laboratorios
              {laboratorios && (
                <Badge className="badge-gradient text-xs">{filtered.length}</Badge>
              )}
            </CardTitle>
            <Button onClick={handleCreate} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
              <Plus className="mr-2 h-4 w-4" /> Nuevo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button variant={showInactive ? 'default' : 'outline'} size="sm" onClick={() => setShowInactive(!showInactive)}
              className={showInactive ? 'bg-teal-800 hover:bg-teal-900 text-white' : ''}>
              {showInactive ? 'Mostrar solo activos' : 'Mostrar todos'}
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filtered.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(lab => (
                    <TableRow key={lab.id}>
                      <TableCell className="font-medium">{lab.nombre}</TableCell>
                      <TableCell>{lab.grupo_nombre}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{lab.descripcion || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={lab.activo} onCheckedChange={() => handleToggleActivo(lab)}
                            className={lab.activo ? 'data-[state=checked]:bg-teal-500' : ''} />
                          <Badge className={lab.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                            {lab.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(lab)} title="Editar">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(lab)} title="Eliminar">
                            <Trash2 className="h-4 w-4 text-gray-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {search ? 'No se encontraron laboratorios.' : 'No hay laboratorios creados.'}
            </div>
          )}
        </CardContent>
      </Card>

      <LaboratorioFormDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        laboratorio={editing}
        grupos={grupos || []}
        onSubmit={async (data) => { await createMutation.mutateAsync(data); setDialogOpen(false) }}
        onUpdate={async (id, data) => { await updateMutation.mutateAsync({ id, data }); setDialogOpen(false) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
